import React, { useMemo } from "react";
import { Container, Text, Sprite } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import {
  getSymbols1SlideTextConfig,
  getSymbols2SlideTextConfig,
} from "./paytableTextConfig";
import { symbolData } from "../../../../SymbolData";
import { formatToCustomLocale } from "../../../../utilities";
import { ASSET_NAME } from "../../../../assets";

const SymbolsSlide = ({ viewportConfig, slideIndex }) => {
  const { t } = useTranslation();
  const { uiSymbolScale, getResponsiveConfig, device, orientation } =
    useDimensions();

  // Device-specific text configuration for each individual symbols slide
  const textConfig = useMemo(() => {
    // slideIndex 0 = Symbols 1, slideIndex 1 = Symbols 2
    if (slideIndex === 0) {
      return getSymbols1SlideTextConfig(device, orientation);
    } else if (slideIndex === 1) {
      return getSymbols2SlideTextConfig(device, orientation);
    }
    // Fallback to Symbols 1 config if slideIndex is unexpected
    return getSymbols1SlideTextConfig(device, orientation);
  }, [slideIndex, device, orientation]);

  // Load sprite sheet for regular symbols (slide 1)
  const symbolsSpriteSheet = useMemo(
    () => PIXI.Assets.get("reel_symbols"),
    []
  );

  // Get button config for responsive spacing
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG),
    [getResponsiveConfig]
  );

  // Determine layout: portrait = 1 column, landscape = 2 columns
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Base font sizes (reduced from 46 to 38 to match FeaturesSlide appearance)
  const baseSubtitleFontSize = 38;
  const subtitleFontSize = useMemo(
    () =>
      Math.round(
        baseSubtitleFontSize *
          viewportConfig.titleFontSizeMultiplier *
          uiSymbolScale
      ),
    [viewportConfig.titleFontSizeMultiplier, uiSymbolScale]
  );

  const baseTitleFontSize = subtitleFontSize * textConfig.titleFontMultiplier;
  const baseDescriptionFontSize = subtitleFontSize * textConfig.descriptionFontMultiplier;

  // Subtitle style (yellow "Symbols" text)
  const subtitleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: subtitleFontSize,
        fontWeight: "bold",
        fill: 0xfeff00,
        align: "center",
      }),
    [subtitleFontSize]
  );

  // Calculate positions
  const subtitleY = subtitleFontSize * 0.3;
  const subtitleSpacing = buttonConfig.height * 0.8;
  const contentStartY = subtitleY + subtitleFontSize + subtitleSpacing;
  const availableHeight =
    viewportConfig.viewportHeight - subtitleFontSize - subtitleSpacing;

  // Get special symbol textures from standalone images (slide 0)
  const wildTexture = useMemo(() => {
    return PIXI.Assets.get(ASSET_NAME.special_symbol_1);
  }, []);

  const coinTexture = useMemo(() => {
    return PIXI.Assets.get(ASSET_NAME.special_symbol_2);
  }, []);

  const shineTexture = useMemo(() => {
    return PIXI.Assets.get("shine");
  }, []);

  // Calculate shine dimensions maintaining aspect ratio
  const shineDimensions = useMemo(() => {
    if (!shineTexture) return { width: 0, height: 0 };
    const nativeWidth = shineTexture.width;
    const nativeHeight = shineTexture.height;
    const scaledWidth = nativeWidth * uiSymbolScale;
    const scaledHeight = nativeHeight * uiSymbolScale;
    return { width: scaledWidth, height: scaledHeight };
  }, [shineTexture, uiSymbolScale]);

  // Calculate symbol image size from config - use subtitleFontSize as base for larger images
  const symbolImageSize = useMemo(() => {
    const baseSize = subtitleFontSize * textConfig.imageSizeMultiplier;
    return Math.round(baseSize);
  }, [subtitleFontSize, textConfig.imageSizeMultiplier]);

  // Calculate content layout - use 50% width for each column in landscape
  const contentMaxWidth = viewportConfig.maxWidth * textConfig.columnWidthMultiplier;

  // Calculate vertical offset
  const portraitTopOffset = availableHeight * textConfig.portraitTopOffset;
  const landscapeTopOffset = availableHeight * textConfig.landscapeTopOffset;

  // Get translated texts for Wild & Coin symbols (Slide 0)
  const wildTitleText = t("special_symbol_image_01_title");
  const wildDescriptionText = t("special_symbol_image_01_description");
  const coinTitleText = t("special_symbol_image_02_title");
  const coinDescriptionText = t("special_symbol_image_02_description");

  // AUTO-SCALING: Title font size with overflow prevention for symbol titles
  const finalTitleFontSize = useMemo(() => {
    if (!viewportConfig || !contentMaxWidth) return baseTitleFontSize;

    const MAX_TITLE_ROWS = 2; // Maximum rows allowed for symbol titles
    const MIN_FONT_SIZE = 12; // Minimum readable font size
    let fontSize = baseTitleFontSize;
    let iterations = 0;
    const maxIterations = 20;

    // Test with the longest title text
    const testTexts = [wildTitleText, coinTitleText];
    const longestText = testTexts.reduce((a, b) => a.length > b.length ? a : b, "");

    while (iterations < maxIterations) {
      const testStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: fontSize,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(fontSize * 1.2),
      });

      const metrics = TextMetrics.measureText(longestText, testStyle);
      const lineHeight = metrics.lineHeight || fontSize * 1.2;
      const actualRows = Math.ceil(metrics.height / lineHeight);

      if (actualRows <= MAX_TITLE_ROWS) break;
      if (fontSize <= MIN_FONT_SIZE) {
        break;
      }

      fontSize = Math.round(fontSize * 0.95);
      iterations++;
    }

    return fontSize;
  }, [viewportConfig, contentMaxWidth, baseTitleFontSize, wildTitleText, coinTitleText]);

  // AUTO-SCALING: Description font size with overflow prevention
  const finalDescriptionFontSize = useMemo(() => {
    if (!viewportConfig || !contentMaxWidth) return baseDescriptionFontSize;

    const MAX_DESCRIPTION_ROWS = isPortrait ? 4 : 5; // More rows in landscape
    const MIN_FONT_SIZE = 12;
    let fontSize = baseDescriptionFontSize;
    let iterations = 0;
    const maxIterations = 20;

    // Test with the longest description text
    const testTexts = [wildDescriptionText, coinDescriptionText];
    const longestText = testTexts.reduce((a, b) => a.length > b.length ? a : b, "");

    while (iterations < maxIterations) {
      const testStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: fontSize,
        fontWeight: "400",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(fontSize * 1.3),
      });

      const metrics = TextMetrics.measureText(longestText, testStyle);
      const lineHeight = metrics.lineHeight || fontSize * 1.3;
      const actualRows = Math.ceil(metrics.height / lineHeight);

      if (actualRows <= MAX_DESCRIPTION_ROWS) break;
      if (fontSize <= MIN_FONT_SIZE) {
        break;
      }

      fontSize = Math.round(fontSize * 0.95);
      iterations++;
    }

    return fontSize;
  }, [viewportConfig, contentMaxWidth, baseDescriptionFontSize, wildDescriptionText, coinDescriptionText, isPortrait]);

  // Calculate gap between texts based on final title size
  const gapBetweenTexts = finalTitleFontSize * textConfig.gapMultiplier;

  // Text styles with auto-scaled font sizes
  const symbolTitleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalTitleFontSize,
        fontWeight: "bold",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(finalTitleFontSize * 1.2),
      }),
    [finalTitleFontSize, contentMaxWidth]
  );

  const symbolDescriptionStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalDescriptionFontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(finalDescriptionFontSize * 1.3),
      }),
    [finalDescriptionFontSize, contentMaxWidth]
  );

  // Calculate positions for symbols - use 50% width columns like features
  const wildX = isPortrait ? 0 : -viewportConfig.maxWidth / 4; // Left column at -25% of maxWidth
  const coinX = isPortrait ? 0 : viewportConfig.maxWidth / 4; // Right column at +25% of maxWidth

  const wildY = isPortrait
    ? contentStartY + portraitTopOffset
    : contentStartY + availableHeight * 0.3 + landscapeTopOffset;

  // Calculate row gap in portrait mode using config
  const portraitRowGap = subtitleFontSize * textConfig.portraitRowGapMultiplier;

  const coinY = isPortrait
    ? wildY + symbolImageSize + finalTitleFontSize + gapBetweenTexts + finalDescriptionFontSize * 3 + portraitRowGap
    : wildY;

  // Get paytable, bet, and baseBet from Redux for slide index 1 (regular symbols)
  const paytable = useSelector((state) => state.game?.paytable);
  const bet = useSelector((state) => state.game?.bet || 1);
  const baseBet = useSelector((state) => state.game?.baseBet || 1);
  const currency = useSelector((state) => state.game?.currency || "");

  // Guard: Don't render until textures are ready (after all hooks)
  // Slide 0 needs special symbol images, Slide 1 needs spritesheet
  if (slideIndex === 0) {
    if (!wildTexture || !coinTexture || !shineTexture) {
      return null;
    }
  } else if (slideIndex === 1) {
    if (!symbolsSpriteSheet?.textures) {
      return null;
    }
  }

  // Render slide index 0 (Wild & Coin)
  if (slideIndex === 0) {
    return renderWildAndCoinSlide();
  }

  // Render slide index 1 (Regular symbols paytable)
  return renderRegularSymbolsSlide();

  function renderWildAndCoinSlide() {
    return (
    <Container name={`secondary_symbols_slide_${slideIndex}`}>
      {/* Subtitle */}
      <Text
        text={t("symbols_title")}
        style={subtitleStyle}
        anchor={{ x: 0.5, y: 0 }}
        x={0}
        y={Math.round(subtitleY)}
        resolution={2}
      />

      {/* Shine Image - Only visible in landscape, centered between symbols */}
      {!isPortrait && shineTexture && (
        <Sprite
          texture={shineTexture}
          anchor={0.5}
          x={0}
          y={Math.round(wildY)}
          width={shineDimensions.width}
          height={shineDimensions.height}
        />
      )}

      {/* WILD Symbol */}
      <Container name="wild_symbol_container" x={Math.round(wildX)} y={0}>
        {/* Wild Image */}
        {wildTexture && (
          <Sprite
            texture={wildTexture}
            anchor={0.5}
            x={0}
            y={Math.round(wildY)}
            width={symbolImageSize}
            height={symbolImageSize}
          />
        )}

        {/* Wild Title */}
        <Text
          text={wildTitleText}
          style={symbolTitleStyle}
          anchor={{ x: 0.5, y: 0 }}
          x={0}
          y={Math.round(wildY + symbolImageSize / 2 + gapBetweenTexts)}
          resolution={2}
        />

        {/* Wild Description */}
        <Text
          text={wildDescriptionText}
          style={symbolDescriptionStyle}
          anchor={{ x: 0.5, y: 0 }}
          x={0}
          y={Math.round(
            wildY + symbolImageSize / 2 + gapBetweenTexts + finalTitleFontSize + gapBetweenTexts * 0.5
          )}
          resolution={2}
        />
      </Container>

      {/* COIN Symbol */}
      <Container name="coin_symbol_container" x={Math.round(coinX)} y={0}>
        {/* Coin Image */}
        {coinTexture && (
          <Sprite
            texture={coinTexture}
            anchor={0.5}
            x={0}
            y={Math.round(coinY)}
            width={symbolImageSize}
            height={symbolImageSize}
          />
        )}

        {/* Coin Title */}
        <Text
          text={coinTitleText}
          style={symbolTitleStyle}
          anchor={{ x: 0.5, y: 0 }}
          x={0}
          y={Math.round(coinY + symbolImageSize / 2 + gapBetweenTexts)}
          resolution={2}
        />

        {/* Coin Description */}
        <Text
          text={coinDescriptionText}
          style={symbolDescriptionStyle}
          anchor={{ x: 0.5, y: 0 }}
          x={0}
          y={Math.round(
            coinY + symbolImageSize / 2 + gapBetweenTexts + finalTitleFontSize + gapBetweenTexts * 0.5
          )}
          resolution={2}
        />
      </Container>
    </Container>
    );
  }

  function renderRegularSymbolsSlide() {
    // Define regular symbols to display (IDs 2-9: h1, m1, m2, l1, l2, l3, l4, l5)
    const regularSymbolIds = [2, 3, 4, 5, 6, 7, 8, 9];

    // Calculate grid layout
    const columns = isPortrait ? 2 : 4;
    const rows = Math.ceil(regularSymbolIds.length / columns);

    // Calculate spacing with row gap from config
    const gridWidth = viewportConfig.maxWidth * (isPortrait ? 0.9 : 0.95);
    const columnWidth = gridWidth / columns;
    const rowGap = subtitleFontSize * (textConfig.rowGapMultiplier || 1.0);
    const totalRowGaps = (rows - 1) * rowGap;
    const rowHeight = (availableHeight - totalRowGaps) / rows;

    // Calculate starting position
    const gridStartY = contentStartY + (isPortrait ? portraitTopOffset : landscapeTopOffset);

    return (
      <Container name={`secondary_symbols_slide_${slideIndex}`}>
        {/* Subtitle */}
        <Text
          text={t("symbols_title")}
          style={subtitleStyle}
          anchor={{ x: 0.5, y: 0 }}
          x={0}
          y={Math.round(subtitleY)}
          resolution={2}
        />

        {/* Regular symbols grid */}
        {regularSymbolIds.map((symbolId, index) => {
          const row = Math.floor(index / columns);
          const col = index % columns;

          // Calculate position with row gap
          const xPos = (col - (columns - 1) / 2) * columnWidth;
          const yPos = gridStartY + row * (rowHeight + rowGap);

          return renderSymbolWithPaytable(symbolId, xPos, yPos, columnWidth);
        })}
      </Container>
    );
  }

  function renderSymbolWithPaytable(symbolId, xPos, yPos, columnWidth) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo?.texture) return null;

    // Get texture
    const texture = symbolsSpriteSheet?.textures?.[`${symbolInfo.texture}.png`];
    if (!texture) return null;

    // Calculate symbol image size (from config)
    const gridSymbolSize = symbolImageSize * textConfig.gridSymbolSizeMultiplier;

    // Get paytable entries for this symbol (EXACT same logic as InfoOverlayController line 565-579)
    const paytableEntries = paytable?.[symbolId] || [];

    // Build display entries (EXACT same logic as InfoOverlayController line 565-579)
    const displayEntries = paytableEntries
      .map((value, idx) => ({ value, originalIndex: idx }))
      .reverse()
      .filter((item) => item.value !== 0)
      .map((item, displayIndex) => {
        const symbolCount = paytableEntries.length - displayIndex;
        const payoutAmount = item.value * (bet / baseBet);
        const formattedPayout = formatToCustomLocale(payoutAmount);
        return {
          symbolCount,
          formattedPayout,
          symbolColor: 0xffff00,
          payoutColor: 0xffffff,
        };
      });

    // Calculate paytable text size (from config) - use final scaled title font
    const paytableFontSize = finalTitleFontSize * textConfig.paytableFontMultiplier;
    const paytableLineHeight = paytableFontSize * 1.2;

    return (
      <Container key={symbolId} name={`symbol_${symbolId}_container`} x={Math.round(xPos)} y={Math.round(yPos)}>
        {/* Symbol Image */}
        <Sprite
          texture={texture}
          anchor={0.5}
          x={0}
          y={0}
          width={gridSymbolSize}
          height={gridSymbolSize}
        />

        {/* Paytable Text */}
        <Container name={`paytable_text_${symbolId}`}>
          {displayEntries.map((entry, idx) => {
            const lineY = gridSymbolSize / 2 + gapBetweenTexts * 0.5 + idx * paytableLineHeight;
            const fullText = `${entry.symbolCount}: ${entry.formattedPayout} ${currency}`.trim();

            const textStyle = new TextStyle({
              fontFamily: FONT_CONFIG.PRIMARY,
              fontSize: paytableFontSize,
              fontWeight: "bold",
              fill: 0xffffff,
              align: "left",
            });

            // Measure actual text width for accurate centering
            const textMetrics = TextMetrics.measureText(fullText, textStyle);
            const actualTextWidth = textMetrics.width;

            // Center the text under the symbol (symbol is at x=0)
            const textX = Math.round(-actualTextWidth / 2);

            return (
              <Text
                key={`paytable_line_${symbolId}_${idx}`}
                text={fullText}
                style={textStyle}
                anchor={{ x: 0, y: 0 }}
                x={textX}
                y={Math.round(lineY)}
                resolution={2}
              />
            );
          })}
        </Container>
      </Container>
    );
  }
};

export default SymbolsSlide;
